"use client";

import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import './enterprise-ai-intelligence.css';

const enterpriseAIImages = {
  hero: "/assets/images/why-is-inventory-increasing.jpg",
  section3: "/assets/images/plm-heart-everything.jpg",
  section6: "/assets/images/key-takeaways.jpg",
  section7: "/assets/images/questions-consider.jpg",
  cta: "/assets/images/don't-start-with-ai 1.jpg",


};

/* Saari styling ab enterprise-ai-intelligence.css mein hai.
   Hero background image (wahi purani Unsplash image) CSS mein hai:
   enterprise-ai-intelligence.css -> ".eai-hero-bg"
   Hero ke saare classes unique hain (eai-hero*), taaki baaki pages ke .hero CSS se clash na ho.

   Site header aur footer is page mein nahi hain (layout se aate hain) */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>('[data-reveal]');

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ '--eai-delay': `${Math.min(i * step, max)}ms` } as CSSProperties);

type IconName =
  | 'question'
  | 'network'
  | 'database'
  | 'chart'
  | 'spark'
  | 'target'
  | 'play'
  | 'cube'
  | 'building'
  | 'people'
  | 'globe'
  | 'clock'
  | 'shield'
  | 'trend'
  | 'trenddown'
  | 'truck'
  | 'gear'
  | 'coins'
  | 'layers'
  | 'chip'
  | 'doc'
  | 'sheet'
  | 'cycle'
  | 'bulb'
  | 'cart'
  | 'factory'
  | 'store';

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    question: <><circle cx="12" cy="12" r="9" /><path d="M9.6 9a2.5 2.5 0 1 1 4.2 1.8c-1 .8-1.8 1.2-1.8 2.7" /><path d="M12 16.8h.01" /></>,
    network: <><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="19" r="2.5" /><circle cx="19" cy="19" r="2.5" /><path d="M12 7.5v4M12 11.5l-6 5.5M12 11.5l6 5.5" /></>,
    database: <><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
    chart: <><path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6" /></>,
    spark: <><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    play: <><circle cx="12" cy="12" r="9" /><path d="m10 8 6 4-6 4z" /></>,
    cube: <><path d="M12 3 4 7.5v9L12 21l8-4.5v-9z" /><path d="M4 7.5 12 12l8-4.5M12 12v9" /></>,
    building: <><path d="M4 21V5l8-2v18M12 21V9l8 2v10" /><path d="M8 9h.01M8 13h.01M8 17h.01M16 14h.01M16 17h.01" /></>,
    people: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    trend: <><path d="M4 17 10 11l4 4 6-8" /><path d="M15 7h5v5" /></>,
    trenddown: <><path d="M4 7 10 13l4-4 6 8" /><path d="M15 17h5v-5" /></>,
    truck: <><path d="M3 7h11v10H3zM14 10h4l3 3v4h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    coins: <><circle cx="12" cy="12" r="9" /><path d="M12 6v12M15 8.5c-.6-.8-1.5-1.2-2.8-1.2-1.7 0-2.8.8-2.8 2 0 3.1 5.8 1.3 5.8 4.3 0 1.3-1.3 2.1-3 2.1-1.3 0-2.4-.4-3-1.2" /></>,
    layers: <><path d="M12 3 3 8l9 5 9-5z" /><path d="m3 12 9 5 9-5" /><path d="m3 16 9 5 9-5" /></>,
    chip: <><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4" /></>,
    doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
    sheet: <><rect x="4" y="4" width="16" height="16" rx="1" /><path d="M4 10h16M4 15h16M10 4v16" /></>,
    cycle: <><path d="M4 9a8 8 0 0 1 14-3l2 2M20 15a8 8 0 0 1-14 3l-2-2" /><path d="M20 4v4h-4M4 20v-4h4" /></>,
    bulb: <><path d="M9 18h6M10 22h4M8.5 14.5A7 7 0 1 1 15.5 14.5c-.8.7-1.2 1.5-1.3 2.5h-4.4c-.1-1-.5-1.8-1.3-2.5Z" /></>,
    cart: <><path d="M3 4h2l2.2 11.2a2 2 0 0 0 2 1.6h8.9a2 2 0 0 0 1.9-1.4L22 8H6" /><circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" /></>,
    factory: <><path d="M3 21V9l6 4V9l6 4V5l6 3v13H3Z" /><path d="M7 17h2M13 17h2M18 17h1" /></>,
    store: <><path d="M4 10V21h16V10M3 10l2-6h14l2 6" /><path d="M3 10a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0M9 21v-6h6v6" /></>,
  };

  return (
    <svg
      className="eai-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const tags = ['FMCG', 'INVENTORY', 'SUPPLY CHAIN', 'ANALYTICS'];

const series = [
  { number: '1', title: 'Business Before AI', desc: 'Why understanding the business comes first.' },
  { number: '2', title: 'Dimensions, Facts and Measures', desc: 'How to structure the enterprise.' },
  { number: '3', title: 'LLM vs TFM', desc: 'Different models for different problems.' },
  { number: '4', title: 'Maths Behind AI', desc: 'A simple view of regression and prediction.' },
  { number: '5', title: 'From Insight to Action', desc: 'Turning intelligence into measurable value.' },
];

const relatedInsights: { icon: IconName; title: string; tone: string }[] = [
  { icon: 'coins', title: 'Profit Is Up. Why Is Cash Down?', tone: 'blue' },
  { icon: 'cube', title: 'Which SKUs are Growing but Diluting Contribution?', tone: 'green' },
  { icon: 'trend', title: 'Demand Planning in Volatile Markets', tone: 'purple' },
  { icon: 'chart', title: 'From Data to Decisions in FMCG', tone: 'amber' },
  { icon: 'chip', title: 'AI and GPU Economics', tone: 'cyan' },
];

const flow: { icon: IconName; label: string; desc: string; tone: string }[] = [
  { icon: 'bulb', label: 'Consumer Insight', desc: 'Turn shopper trends and preferences into winning style bets.', tone: 'blue' },
  { icon: 'cube', label: 'Range & Assortment', desc: 'Plan the right styles, colours and sizes for every store and channel.', tone: 'blue' },
  { icon: 'cart', label: 'Plan & Buy', desc: 'Make smarter buying decisions with real-time demand signals.', tone: 'blue' },
  { icon: 'factory', label: 'Source & Produce', desc: 'Collaborate with vendors and bring collections to life on time.', tone: 'blue' },
  { icon: 'truck', label: 'Distribute & Allocate', desc: 'Get the right stock to the right store, fast.', tone: 'blue' },
  { icon: 'store', label: 'Sell (Omnichannel)', desc: 'Deliver a seamless shopping experience across every channel.', tone: 'blue' },
  { icon: 'people', label: 'Customer & Loyalty', desc: 'Turn every purchase into a lasting relationship.', tone: 'blue' },
];

/* Chart data: sales bars ki height %, inventory line ki height % (12 months) */
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const salesBars = [88, 91, 90, 86, 84, 88, 76, 78, 83, 99, 87, 98];
const inventoryLine = [39, 40, 43, 42, 49, 55, 61, 71, 76, 84, 89, 97];
const yTicks = ['200', '150', '100', '50'];

const dimensions: { icon: IconName; label: string }[] = [
  { icon: 'cube', label: 'Product / SKU' },
  { icon: 'building', label: 'Plant / Warehouse' },
  { icon: 'people', label: 'Channel / Customer' },
  { icon: 'globe', label: 'Region / Market' },
  { icon: 'clock', label: 'Time / Seasonality' },
  { icon: 'chart', label: 'Demand vs Supply' },
  { icon: 'shield', label: 'Policies / Parameters' },
];

const drivers: { icon: IconName; label: string }[] = [
  { icon: 'trend', label: 'Over-forecasting' },
  { icon: 'trenddown', label: 'Slower demand' },
  { icon: 'spark', label: 'New product launches' },
  { icon: 'truck', label: 'Channel destocking' },
  { icon: 'gear', label: 'Supply normalisation' },
  { icon: 'cube', label: 'Batch / packing changes' },
  { icon: 'coins', label: 'Commercial incentives' },
];

const actions: { icon: IconName; title: string; desc: string }[] = [
  { icon: 'gear', title: 'Improve planning accuracy', desc: 'Use better demand signals and collaboration.' },
  { icon: 'cube', title: 'Optimise assortment and mix', desc: 'Focus on high-velocity, profitable SKUs.' },
  { icon: 'truck', title: 'Align supply with demand', desc: 'Adjust production, procurement and replenishment.' },
  { icon: 'people', title: 'Review commercial policies', desc: 'Check incentives, order patterns and channel dynamics.' },
];

const takeaways: { icon: IconName; text: string; tone: string }[] = [
  { icon: 'trend', text: 'Look beyond basic numbers to understand the real drivers.', tone: 'blue' },
  { icon: 'layers', text: 'Bring together data across functions and channels.', tone: 'green' },
  { icon: 'chip', text: 'Use analytics and AI to identify patterns and scenarios.', tone: 'purple' },
  { icon: 'target', text: 'Convert insights into clear decisions and actions.', tone: 'amber' },
  { icon: 'clock', text: 'Track outcomes and keep learning.', tone: 'cyan' },
];

const questions = [
  'Which product groups are driving the increase?',
  'Is this a demand or supply issue?',
  'Are commercial policies creating unintended behaviour?',
  'What is the impact on cash and working capital?',
  'What actions can we take in the next 30–60 days?',
];

const downloads: { icon: IconName; label: string; tone: string }[] = [
  { icon: 'doc', label: 'Framework: Inventory Analysis (PDF)', tone: 'rose' },
  { icon: 'sheet', label: 'Checklist: 20 Questions to Ask (XLSX)', tone: 'green' },
  { icon: 'sheet', label: 'Sample Analysis Template (XLSX)', tone: 'green' },
];

const problems: { n: string; icon: IconName; title: string }[] = [
  { n: '01', icon: 'chart', title: 'Demand Forecasting is Inaccurate' },
  { n: '07', icon: 'coins', title: 'Profit Is Up. Why Is Cash Down?' },
  { n: '12', icon: 'cube', title: 'Which SKUs Are Growing but Diluting Contribution?' },
  { n: '18', icon: 'cycle', title: 'How Do We Improve Inventory Turns?' },
  { n: '24', icon: 'network', title: 'How Do We Optimise Channel Performance?' },
];

/* inventory line ke points (viewBox 0 0 100 100), x = har month ka center */
const linePoints = inventoryLine
  .map((h, i) => `${(((i + 0.5) / inventoryLine.length) * 100).toFixed(2)},${100 - h}`)
  .join(' ');

/* ============================== PAGE ============================== */

export default function EnterpriseAIIntelligence() {
  const ref = useScrollReveal();

  return (
    <div className="eai-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="eai-hero" data-purpose="hero-section">
          <div className="eai-hero-bg"></div>
          <div className="eai-hero-overlay"></div>
          <div className="eai-hero-grid"></div>
          <div className="eai-hero-glow eai-hero-glow--left"></div>
          <div className="eai-hero-glow eai-hero-glow--right"></div>

          <div className="op-container op-container--left eai-hero-inner">
            <div className="eai-hero-layout">
              <div className="eai-hero-content">
                <h1 className="eai-hero-title" data-reveal style={delay(0)}>
                  Why is inventory increasing
                  <br />
                  when sales aren’t?
                </h1>

                <p className="eai-hero-text" data-reveal style={delay(1, 150)}>
                  A practical way to understand the real reasons behind inventory build-up — and how
                  data, analytics and AI can help you take action.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== THE ENTERPRISE AI MAP ===================== */}
        <section className="op-section op-section--white" data-purpose="ai-map-series">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <h2 className="sec-title">The Enterprise AI Map</h2>
              <p className="sec-sub sec-sub--base">Five parts, one path from business questions to measurable value.</p>
            </div>

            <div className="series-grid">
              {series.map((s, i) => (
                <div className="series-card" key={s.number} data-reveal style={delay(i, 90)}>
                  <span className="series-num">{s.number}</span>
                  <h3 className="series-title">{s.title}</h3>
                  <p className="series-desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================= QUESTION + PROBLEM ======================= */}
        <section className="op-section op-section--slate" data-purpose="business-question">
          <div className="op-container">
            <div className="lead-section-heading" data-reveal style={delay(1, 100)}>
              <h2 className="lead-section-title">From Business Question to Action</h2>
            </div>

            <div className="lead-grid">
              <div className="lead-card" data-reveal>
                <span className="lead-num">1</span>
                <h2 className="lead-title">The Business Question</h2>
                <p className="lead-text">
                  Inventory is increasing, but sales are flat or declining. Why is this happening, and
                  what can we do about it?
                </p>
              </div>
              <div className="lead-card" data-reveal style={delay(1, 150)}>
                <span className="lead-num">2</span>
                <h2 className="lead-title">Understanding the Problem</h2>
                <p className="lead-text">
                  Higher inventory is often a symptom, not the real problem. It can be driven by demand
                  changes, planning gaps, supply disruptions, portfolio shifts, channel dynamics or
                  commercial decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= PLM AT THE HEART OF EVERYTHING ================= */}
        <section className="op-section op-section--white" data-purpose="structured-approach">
          <div className="plm-container">
            <div className="plm-heading" data-reveal>
              <span className="plm-heading-number" aria-hidden="true">3</span>
              <div className="plm-heading-copy">
                <h2 className="plm-title plm-title--lead">PLM at the Heart of Everything</h2>
                <p className="plm-subtitle">From idea to impact. A connected product and business lifecycle.</p>
              </div>
            </div>

            <div className="plm-layout">
              <div className="plm-image-wrap" data-reveal>
                <img src={enterpriseAIImages.hero} alt="Connected fashion product lifecycle" className="plm-image" />
              </div>

              <div className="plm-flow" aria-label="Product lifecycle steps">
                {flow.map((step, i) => (
                  <div className="plm-step" key={step.label} data-reveal style={delay(i, 80, 480)}>
                    <span className="plm-step-number">{String(i + 1).padStart(2, '0')}</span>
                    <span className="plm-step-icon">
                      <Icon name={step.icon} />
                    </span>
                    <div className="plm-step-copy">
                      <h3 className="plm-step-title">{step.label}</h3>
                      <p className="plm-step-desc">{step.desc}</p>
                    </div>
                    <span className="plm-step-arrow" aria-hidden="true">→</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= WHAT THE DATA TYPICALLY SHOWS ================= */}
        <section className="op-section op-section--slate" data-purpose="data-analysis">
          <div className="op-container">
            <div className="opview-head" data-reveal>
              <div>
                <h2 className="sec-title">What the Data Typically Shows</h2>
                <p className="sec-sub sec-sub--base">
                  Illustrative analysis (not from a specific customer).
                </p>
              </div>
            </div>

            <div className="cockpit" data-reveal style={delay(1, 150)}>
              <div className="cockpit-bar">
                <div className="cockpit-bar-left">
                  <div className="cockpit-light cockpit-light--red"></div>
                  <div className="cockpit-light cockpit-light--amber"></div>
                  <div className="cockpit-light cockpit-light--green"></div>
                  <span className="cockpit-name">ORANE-AI-VIEW // SALES VS INVENTORY</span>
                </div>
                <div className="cockpit-bar-right">
                  <span className="cockpit-chip">MODE: ILLUSTRATIVE</span>
                </div>
              </div>

              <div className="data-grid">
                <div className="chart-panel">
                  <div className="legend">
                    <span className="legend-item">
                      <i className="legend-swatch legend-swatch--sales"></i> Sales
                    </span>
                    <span className="legend-item">
                      <i className="legend-swatch legend-swatch--inventory"></i> Inventory
                    </span>
                  </div>

                  <div className="chart">
                    <div className="chart-yaxis">
                      {yTicks.map((t) => (
                        <span key={t}>{t}</span>
                      ))}
                    </div>
                    <div className="chart-plot">
                      <div className="chart-grid">
                        {yTicks.map((t) => (
                          <i key={t}></i>
                        ))}
                      </div>
                      <div className="chart-bars">
                        {salesBars.map((h, i) => (
                          <div className="chart-col" key={months[i]}>
                            <span className="chart-bar" style={{ height: `${h}%` }}></span>
                          </div>
                        ))}
                      </div>
                      <svg
                        className="chart-line"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        role="img"
                        aria-label="Inventory trend rising through the year"
                      >
                        <polyline points={linePoints} />
                      </svg>
                      {inventoryLine.map((h, i) => (
                        <span
                          className="chart-dot"
                          key={months[i]}
                          style={{ left: `${((i + 0.5) / inventoryLine.length) * 100}%`, bottom: `${h}%` }}
                        ></span>
                      ))}
                    </div>
                    <div className="chart-months">
                      {months.map((m) => (
                        <span key={m}>{m}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="side-panel">
                  <h3 className="side-title">Key dimensions to analyse</h3>
                  {dimensions.map((d) => (
                    <div className="side-row" key={d.label}>
                      <span className="side-icon side-icon--blue">
                        <Icon name={d.icon} />
                      </span>
                      {d.label}
                    </div>
                  ))}
                </div>

                <div className="side-panel">
                  <h3 className="side-title">Common drivers</h3>
                  {drivers.map((d) => (
                    <div className="side-row" key={d.label}>
                      <span className="side-icon side-icon--amber">
                        <Icon name={d.icon} />
                      </span>
                      {d.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================== FROM INSIGHT TO ACTION ======================== */}
        <section className="op-section op-section--white" data-purpose="insight-to-action">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <h2 className="sec-title">From Insight to Action</h2>
              <p className="sec-sub">Example areas of action (will vary by business).</p>
            </div>

            <div className="coverage-grid">
              {actions.map((a, i) => (
                <div className="coverage-card" key={a.title} data-reveal style={delay(i, 100)}>
                  <div>
                    <div className="coverage-icon">
                      <Icon name={a.icon} />
                    </div>
                    <h3 className="coverage-title">{a.title}</h3>
                    <p className="coverage-desc">{a.desc}</p>
                  </div>
                  <div className="coverage-foot">
                    <span>Example Action</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =================== KEY TAKEAWAYS =================== */}
        <section className="op-section op-section--slate takeaway-feature-section" data-purpose="key-takeaways">
          <div className="op-container">
            <div className="takeaway-feature-layout">
              <div className="takeaway-feature-image" data-reveal>
                <img
                  src={enterpriseAIImages.section3}
                  alt="Retail product lifecycle and inventory"
                  className="takeaway-feature-img"
                />
              </div>
              <div className="pipeline takeaway-feature-card" data-reveal style={delay(1, 150)}>
                <h3 className="pipeline-head">
                  <span>Key Takeaways</span>
                  <span className="pipeline-count">{takeaways.length} Points</span>
                </h3>
                <div className="takeaway-feature-list">
                  {takeaways.map((row) => (
                    <div className="pipeline-row pipeline-row--top" key={row.text}>
                      <div className={`pipeline-num tone-${row.tone}`}>
                        <Icon name={row.icon} />
                      </div>
                      <div className="pipeline-body">
                        <div className="pipeline-label">{row.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =================== QUESTIONS TO CONSIDER =================== */}
        <section className="op-section op-section--white takeaway-feature-section" data-purpose="questions-to-consider">
          <div className="op-container">
            <div className="takeaway-feature-layout takeaway-feature-layout--reverse">
              <div className="takeaway-feature-image" data-reveal>
                <img
                  src={enterpriseAIImages.section7}
                  alt="Retail analytics and decision-making"
                  className="takeaway-feature-img"
                />
              </div>
              <div className="pipeline takeaway-feature-card" data-reveal style={delay(1, 150)}>
                <h3 className="pipeline-head">
                  <span>Questions to Consider</span>
                  <span className="pipeline-count">{questions.length} Questions</span>
                </h3>
                <div className="takeaway-feature-list">
                  {questions.map((q) => (
                    <div className="pipeline-row pipeline-row--top" key={q}>
                      <div className="pipeline-num tone-blue">?</div>
                      <div className="pipeline-body">
                        <div className="pipeline-label">{q}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== RELATED INSIGHTS + QUOTE + DOWNLOADS ============== */}
        <section className="op-section op-section--white" data-purpose="related-insights">
          <div className="op-container">
            <div className="ladder-grid">
              <div className="pipeline related-timeline-card" data-reveal>
                <h3 className="pipeline-head">
                  <span>Related Insights</span>
                  <span className="pipeline-count">{relatedInsights.length} Articles</span>
                </h3>
                <div className="insight-timeline">
                  {relatedInsights.map((r, index) => (
                    <a className="insight-timeline-row" href="#" key={r.title}>
                      <span className="insight-timeline-number">{String(index + 1).padStart(2, "0")}</span>
                      <span className="insight-timeline-title">{r.title}</span>
                      <span className="insight-timeline-arrow" aria-hidden="true">→</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="side-stack">
                <blockquote className="quote-card" data-reveal style={delay(1, 150)}>
                  <span className="quote-mark">“</span>
                  <p className="quote-text">
                    Good questions create better data. Better data creates better decisions.
                  </p>
                  <cite className="quote-cite">— Orane</cite>
                </blockquote>

                <div className="pipeline" data-reveal style={delay(2, 150)}>
                  <h3 className="pipeline-head">
                    <span>Download</span>
                    <span className="pipeline-count">{downloads.length} Files</span>
                  </h3>
                  {downloads.map((d) => (
                    <a className="pipeline-row" href="#" key={d.label}>
                      <div className={`pipeline-num tone-${d.tone}`}>
                        <Icon name={d.icon} />
                      </div>
                      <div className="pipeline-label">{d.label}</div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================= RELATED 52 PROBLEMS ======================= */}
        <section className="op-section op-section--slate" data-purpose="related-problems">
          <div className="op-container">
            <div className="problems-head" data-reveal>
              <div className="sec-head problems-head-text">
                <div className="sec-badge">Related 52 Problems</div>
                <h2 className="sec-title sec-title--sm">Explore Other Enterprise Problems</h2>
                <p className="sec-sub sec-sub--base">Explore other enterprise problems.</p>
              </div>
              <a className="btn-small" href="/52problem#all-problems" data-reveal style={delay(1, 150)}>
                View All 52 Problems
                <Arrow />
              </a>
            </div>

            <div className="problem-grid">
              {problems.map((p, i) => (
                <a className="problem-card" href="#" key={p.n} data-reveal style={delay(i % 5, 90)}>
                  <div className="problem-icon">
                    <Icon name={p.icon} />
                  </div>
                  <span className="problem-num">{p.n}</span>
                  <h3 className="problem-title">{p.title}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
       {/* ========================= FINAL CTA ================================ */}
<section className="eai-cta" data-purpose="final-call-to-action" id="contact">
  <div className="op-container eai-cta-inner">
    <span className="eai-cta-label" data-reveal>A MORE VALUABLE TOMORROW. TOGETHER.</span>
    <h2 className="eai-cta-title" data-reveal style={delay(1, 120)}>
      Don’t Start With AI.
      <br />
      Start With a Business Problem.
    </h2>
    <p className="eai-cta-text" data-reveal style={delay(2, 120)}>
      Bring us a problem, a decision or an exception that matters. We’ll work backwards from the business
      outcome to the context, data and intelligence required.
    </p>
    <div className="eai-cta-actions" data-reveal style={delay(3, 120)}>
      <a className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
        Tell Us Your Problem
        <Arrow />
      </a>
    </div>
  </div>
</section>
      </main>
    </div>
  );
}